import React, { useState, useCallback, useEffect, useRef } from 'react';
import Header from '../../components/common/global-top-bar';
import ReviewPageControls, { FilterOptionsType, FilterType } from './common/review-page-controls';
import { ListenerDataType, CountResponseType } from './common/types';
import Spinner from '../../components/common/spinner';
import { DropDown } from '../../components/common';
import SwitchBar from '../../components/switch-bar';
import { getListenerProfileUrl, HeaderOptions } from './common/listener-review-constants';
import DOMPurify from 'dompurify';
import { ListenerRole, usePeers } from '../../hooks/usePeers';
import { useUpdateUser } from '../../hooks/useUsers';
import { toast } from 'react-toastify';

const aboutMeOptions: FilterOptionsType = {
  pending: 'Needs Approval',
  approved: 'Approved',
  rejected: 'Rejected',
  all: 'All',
};

const MAX_CHARACTERS = 1000;

type HandleSaveType = (userId: number, aboutMeStatus: string, editedProposedAboutMe?: string) => void;

// Helper to determine if item is approved
const isItemApproved = (aboutMe: string, proposedAboutMe: string, aboutMeRejected: boolean): boolean => {
  return !!aboutMe && !proposedAboutMe && !aboutMeRejected;
};

// Helper to evaluate about me state
const evaluateAboutMeState = (aboutMe: string, proposedAboutMe: string, aboutMeRejected: boolean): string => {
  if (aboutMeRejected) return 'rejected';
  if (proposedAboutMe || !aboutMe) return 'pending';
  return 'approved';
};

// Helper to get textarea styling classes
const getTextareaClasses = (isOverLimit: boolean, hasChanges: boolean): string => {
  const baseClasses = 'w-full min-h-[100px] p-2 border rounded-md focus:outline-none focus:ring-2';

  if (isOverLimit) {
    return `${baseClasses} border-red-500 focus:ring-red-500`;
  }
  if (hasChanges) {
    return `${baseClasses} border-yellow-500 focus:ring-yellow-500 bg-yellow-50`;
  }
  return `${baseClasses} border-gray-300 focus:ring-blue-500`;
};

// Helper to get save button styling classes
const getSaveButtonClasses = (isOverLimit: boolean, hasChanges: boolean, isEditing: boolean): string => {
  const baseClasses = 'w-auto rounded-lg px-3 py-1 text-white';

  if (isOverLimit) {
    return `${baseClasses} bg-gray-400 cursor-not-allowed`;
  }
  if (hasChanges && isEditing) {
    return `${baseClasses} bg-yellow-500 hover:bg-yellow-600`;
  }
  return `${baseClasses} bg-bright-blue hover:bg-blue-600`;
};

interface TableRowProps {
  aboutMe: string;
  proposedAboutMe: string;
  aboutMeRejected: boolean;
  firstName: string;
  lastName: string;
  displayName: string;
  userId: number;
  listenerId: number;
  handleSave: HandleSaveType;
  onContentChange: (userId: number, content: string) => void;
  editedContent: string;
  isEditing: boolean;
  onStartEdit: (userId: number) => void;
  onCancelEdit: (userId: number) => void;
}

const TableRow: React.FunctionComponent<TableRowProps> = ({
  aboutMe,
  proposedAboutMe,
  aboutMeRejected,
  firstName,
  lastName,
  displayName,
  userId,
  listenerId,
  handleSave,
  onContentChange,
  editedContent,
  isEditing,
  onStartEdit,
  onCancelEdit,
}): JSX.Element => {
  const [aboutMeApprovalStatus, setAboutMeApprovalStatus] = useState<string>(() =>
    evaluateAboutMeState(aboutMe, proposedAboutMe, aboutMeRejected),
  );

  const purifiedProposedAboutMe = DOMPurify.sanitize(proposedAboutMe, { ALLOWED_TAGS: ['br'] });
  const purifiedAboutMe = DOMPurify.sanitize(aboutMe, { ALLOWED_TAGS: ['br'] });

  const characterCount = editedContent.length;
  const isOverLimit = characterCount > MAX_CHARACTERS;
  const hasChanges = editedContent !== proposedAboutMe;
  const itemIsApproved = isItemApproved(aboutMe, proposedAboutMe, aboutMeRejected);

  return (
    <tr className="w-full border-b px-7 text-left align-top">
      <td className="align-top"></td>
      <td className="align-top">
        <a
          href={getListenerProfileUrl(listenerId, userId)}
          target="_blank"
          rel="noopener noreferrer"
          className="block cursor-pointer"
        >
          <div>
            {firstName} {lastName}
          </div>
          <div>aka {displayName ? displayName : <i className="text-gray-500">--</i>}</div>
        </a>
      </td>
      <td className="align-top">
        {isEditing ? (
          <div className="relative">
            <textarea
              value={editedContent}
              onChange={(e) => onContentChange(userId, e.target.value)}
              className={getTextareaClasses(isOverLimit, hasChanges)}
              placeholder="No proposed about me"
              autoFocus
            />
            <div className="mt-1 flex items-center justify-between">
              <div className={`text-xs ${isOverLimit ? 'font-semibold text-red-500' : 'text-gray-500'}`}>
                {characterCount} / {MAX_CHARACTERS} characters
                {isOverLimit && <span className="ml-2">⚠ Exceeds limit</span>}
              </div>
              <button
                type="button"
                onClick={() => onCancelEdit(userId)}
                className="text-xs text-gray-600 underline hover:text-gray-800"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : itemIsApproved ? (
          <div className="min-h-[60px] rounded-md border border-gray-200 bg-gray-50 p-2">
            {purifiedProposedAboutMe ? (
              <div dangerouslySetInnerHTML={{ __html: purifiedProposedAboutMe }} />
            ) : (
              <i className="text-gray-400">No proposed content</i>
            )}
          </div>
        ) : (
          <div
            onClick={() => onStartEdit(userId)}
            className="min-h-[60px] cursor-pointer rounded-md border border-transparent p-2 transition-colors hover:border-gray-300 hover:bg-gray-50"
            title="Click to edit"
          >
            {purifiedProposedAboutMe ? (
              <div dangerouslySetInnerHTML={{ __html: purifiedProposedAboutMe }} />
            ) : (
              <i className="text-gray-400">No proposed about me - Click to add</i>
            )}
            <div className="mt-1 text-xs text-gray-400">✎ Click to edit</div>
          </div>
        )}
      </td>
      <td className="align-top">
        <div className="flex">
          <div>
            <DropDown Label="" value={aboutMeApprovalStatus} setValue={setAboutMeApprovalStatus}>
              {Object.keys(aboutMeOptions)
                .filter((value: string) => value !== 'all')
                .map((value: string) => (
                  <option key={value} value={value}>
                    {aboutMeOptions[value]}
                  </option>
                ))}
            </DropDown>
          </div>
          <div className="pl-5">
            <button
              type="button"
              className={getSaveButtonClasses(isOverLimit, hasChanges, isEditing)}
              onClick={() => {
                if (aboutMeApprovalStatus && !isOverLimit) {
                  handleSave(userId, aboutMeApprovalStatus, editedContent);
                }
              }}
              disabled={isOverLimit}
              title={isOverLimit ? 'Cannot save: character limit exceeded' : 'Save changes'}
            >
              Save
            </button>
          </div>
        </div>
      </td>
      <td className="py-4 align-top">
        <div dangerouslySetInnerHTML={{ __html: purifiedAboutMe || '<i>No approved about me</i>' }} />
      </td>
      <td className="align-top">
        <a
          href={getListenerProfileUrl(listenerId, userId)}
          target="_blank"
          rel="noopener noreferrer"
          className="cursor-pointer"
        >
          {userId}
        </a>
      </td>
    </tr>
  );
};

type AboutMeReviewProps = {
  listeners: ListenerDataType[];
  listenersCount: CountResponseType;
  pending: boolean;
};

type AboutMeFilterType = {
  page: number;
  limit: number;
  search: string;
  aboutMeStatus?: string;
  hideIncompletePeers: boolean;
};

const AboutMeReview: React.FunctionComponent<AboutMeReviewProps> = (props): JSX.Element => {
  const { updateUser } = useUpdateUser();
  const [filters, setFilters] = useState<AboutMeFilterType>({
    page: 1,
    limit: 25,
    search: '',
    aboutMeStatus: 'pending',
    hideIncompletePeers: true,
  });

  // Track edited content for each user
  const [editedContents, setEditedContents] = useState<Record<number, string>>({});
  // Track which rows are in edit mode
  const [editingRows, setEditingRows] = useState<Record<number, boolean>>({});

  // Track which users have been initialized to prevent re-initialization
  const initializedUsersRef = useRef<Set<number>>(new Set());

  const { data: { data: listeners } = {}, isLoading } = usePeers({ ...filters });

  // Initialize edited contents when listeners data loads - using ref to track initialization
  useEffect(() => {
    if (listeners && listeners.length > 0) {
      const initialContents: Record<number, string> = {};

      listeners.forEach((listener: ListenerRole) => {
        const userId = listener.user.id;
        // Only initialize if we haven't initialized this user before
        if (!initializedUsersRef.current.has(userId)) {
          // If proposed is empty, use current (about_me) content
          initialContents[userId] = listener.proposed_about_me || listener.about_me || '';
          initializedUsersRef.current.add(userId);
        }
      });

      if (Object.keys(initialContents).length > 0) {
        setEditedContents((prev) => ({ ...prev, ...initialContents }));
      }
    }
  }, [listeners]);

  const handleStartEdit = useCallback((userId: number) => {
    setEditingRows((prev) => ({ ...prev, [userId]: true }));
  }, []);

  const handleCancelEdit = useCallback(
    (userId: number) => {
      // Reset to original content
      const listener = listeners?.find((l: ListenerRole) => l.user.id === userId);
      if (listener) {
        setEditedContents((prev) => ({
          ...prev,
          [userId]: listener.proposed_about_me || listener.about_me || '',
        }));
      }
      setEditingRows((prev) => ({ ...prev, [userId]: false }));
    },
    [listeners],
  );

  const handleContentChange = useCallback((userId: number, content: string) => {
    setEditedContents((prev) => ({ ...prev, [userId]: content }));
  }, []);

  const updateFilter = useCallback(
    (filter: FilterType) => {
      setFilters((prev: AboutMeFilterType) => {
        return {
          page: filter.page,
          limit: filter.limit,
          search: filter.search,
          aboutMeStatus: filter.filterValue !== 'all' ? filter.filterValue : undefined,
          hideIncompletePeers: true,
        };
      });
    },
    [setFilters],
  );

  const getSelectedUserData = useCallback(
    (userId: number) => {
      return listeners?.find((listener: ListenerRole) => listener.user.id === userId);
    },
    [listeners],
  );

  const cleanupUserEditState = useCallback((userId: number) => {
    setEditingRows((prev) => ({ ...prev, [userId]: false }));
    initializedUsersRef.current.delete(userId);
  }, []);

  const handleSaveRejected = useCallback(
    (userId: number) => {
      const payload = {
        listener_role: { about_me_rejected: true },
      };
      updateUser.mutate(
        { userId, data: payload },
        {
          onSuccess: () => {
            toast.success('Profile updated successfully');
            setEditingRows((prev) => ({ ...prev, [userId]: false }));
          },
        },
      );
    },
    [updateUser],
  );

  const handleSaveApprove = useCallback(
    (userId: number, editedProposedAboutMe?: string) => {
      const selectedUserData = getSelectedUserData(userId);
      const contentToApprove =
        editedProposedAboutMe ?? selectedUserData?.proposed_about_me ?? selectedUserData?.about_me ?? '';

      const payload = {
        listener_role: { about_me: contentToApprove },
      };

      updateUser.mutate(
        { userId, data: payload },
        {
          onSuccess: () => {
            toast.success('Profile updated successfully');
            cleanupUserEditState(userId);
            setEditedContents((prev) => {
              const newContents = { ...prev };
              delete newContents[userId];
              return newContents;
            });
          },
        },
      );
    },
    [updateUser, getSelectedUserData, cleanupUserEditState],
  );

  const handleSavePending = useCallback(
    (userId: number, editedProposedAboutMe?: string) => {
      const selectedUserData = getSelectedUserData(userId);
      const contentToPend = editedProposedAboutMe ?? selectedUserData?.about_me ?? '';

      const payload = {
        listener_role: {
          proposed_about_me: contentToPend,
          about_me_rejected: false,
        },
      };

      updateUser.mutate(
        { userId, data: payload },
        {
          onSuccess: () => {
            toast.success('Profile updated successfully');
            setEditingRows((prev) => ({ ...prev, [userId]: false }));
            setEditedContents((prev) => ({ ...prev, [userId]: contentToPend }));
          },
        },
      );
    },
    [updateUser, getSelectedUserData],
  );

  const handleSave: HandleSaveType = useCallback(
    (userId, aboutMeStatus, editedProposedAboutMe) => {
      const saveHandlers = {
        approved: handleSaveApprove,
        pending: handleSavePending,
        rejected: handleSaveRejected,
      };

      const handler = saveHandlers[aboutMeStatus as keyof typeof saveHandlers];
      if (handler) {
        handler(userId, editedProposedAboutMe);
      }
    },
    [handleSaveApprove, handleSavePending, handleSaveRejected],
  );

  return (
    <div>
      {isLoading && <Spinner />}
      <Header heading={'About Me'} />
      <SwitchBar heading={HeaderOptions} position={4} />
      <ReviewPageControls
        updateFilter={updateFilter}
        count={listeners?.length}
        filterOptions={aboutMeOptions}
        defaultFilterValue="pending"
      />

      <div className="max-window-height overflow-y-auto px-7">
        <div className="table-overflow">
          <table className="table-row-format relative w-full table-fixed border-collapse">
            <thead className="h-full bg-white">
              <tr className="w-full px-7 text-left">
                <th className="sticky top-0 mb-4 h-full w-8 bg-white text-left"></th>
                <th className="sticky top-0 mb-4 h-full w-64 bg-white py-2">Name</th>
                <th className="sticky top-0 mb-4 h-full w-80 bg-white text-left">Proposed</th>
                <th className="sticky top-0 mb-4 h-full w-80 bg-white text-left">Action</th>
                <th className="sticky top-0 mb-4 h-full w-80 bg-white">Current</th>
                <th className="w-15 sticky top-0 mb-4 h-full bg-white">Peer ID</th>
              </tr>
            </thead>
            <tbody>
              <tr className="h-2"></tr>
              {listeners &&
                listeners?.length > 0 &&
                listeners.map((listenerData: ListenerRole) => (
                  <TableRow
                    key={listenerData?.id}
                    aboutMe={listenerData?.about_me || ''}
                    proposedAboutMe={listenerData?.proposed_about_me || ''}
                    aboutMeRejected={listenerData?.about_me_rejected}
                    lastName={listenerData?.user?.last_name || ''}
                    firstName={listenerData?.user?.first_name || ''}
                    displayName={listenerData?.user.display_name}
                    userId={listenerData?.user.id}
                    listenerId={listenerData?.id}
                    handleSave={handleSave}
                    onContentChange={handleContentChange}
                    editedContent={editedContents[listenerData?.user.id] ?? listenerData?.proposed_about_me ?? ''}
                    isEditing={editingRows[listenerData?.user.id] || false}
                    onStartEdit={handleStartEdit}
                    onCancelEdit={handleCancelEdit}
                  />
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AboutMeReview;
